// Planning · Capaciteit, met de markup van het prototype (Capaciteit.dc.html en de varianten Achterstand, Procent,
// Herverdelen, Export en Medewerker): per persoon en week de bezetting uit Clockify. De server rekent de load uit
// (services/capacity.py): schatting min gelogd, gespreid over de werkdagen tot de Planner-deadline; wat over deadline
// is, is achterstand en die verdeelt dit scherm over de komende weken. Support telt als vaste reservering mee. Alleen
// afwijkingen krijgen kleur, met de drempels uit Instellingen.
// Een klik op een cel toont de taken van die week, op Achterstand of Zonder schatting die taken. De planner met
// Project-licentie geeft daar een taak aan een collega met ruimte, schuift ze een week op of zet een nieuwe deadline;
// een schatting gaat naar Clockify en kan dus ook zonder licentie; inzetbaarheid voor één week komt in Teams terecht.
// De klant uit de bovenbalk beperkt de uren tot de taken van die klant, zonder support. De medewerker bekijkt alleen,
// en enkel als Instellingen (Rollen en rechten) dat toelaat.
// Eenheid, achterstand, support en team onthoudt de browser.
import { useQueryClient } from "@tanstack/react-query";
import { Fragment, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router";
import { ApiError } from "../lib/api";
import {
  clockifyApi, fmtHours, loadTone, useCapacity, useClockifyLive, useClockifyWrite, weekLoad,
  type Absence, type Capacity, type CapOptions, type CapPerson, type ClockifyStatus, type TaskHours,
} from "../lib/api-clockify";
import { INZET_PCTS, NO_TEAM, nOf, periodAt, teamOfPerson, teamsApi, useTeams, useTeamWrite, type InzetPeriod, type Team } from "../lib/api-teams";
import { fridays } from "../lib/board";
import { count, dayDate, endOps, when } from "../lib/cleanup";
import { useIndex, useMe, usePlanning, useTaskOps } from "../lib/hooks";
import { Icon } from "../lib/icons";
import { isBool, usePref } from "../lib/prefs";
import { addDays, ddmm, deadline, dow, firstName, initials, plannerTaskUrl } from "../lib/planning";
import { rightsOf, useSettings } from "../lib/settings";
import type { Me, Person, PlanningData, Task, TaskOp } from "../lib/types";
import { TaskDrawer } from "../components/TaskDrawer";
import { Av } from "../components/Avatar";
import { useAppState } from "../shell/AppState";
import { LoadError } from "../shell/Layout";
import { useToast } from "../shell/Toast";
import { PlanningTabs } from "./PlanningTabs";

type Unit = "uren" | "pct";
type Open = { kind: "cell"; personId: string; week: number } | { kind: "backlog"; personId: string } | { kind: "noest"; personId: string };
type Index = ReturnType<typeof useIndex>;
interface Thresholds { room: number; full: number; over: number }

const SPREADS = [2, 4, 6, 8];
const EST_PICKS = [4, 8, 16, 24, 40];

// ---------- keuzes die de browser onthoudt (lib/prefs.ts) ----------

const isUnit = (v: unknown): v is Unit => v === "uren" || v === "pct";
const isNum = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v) && v >= 0;
const isTeam = (v: unknown): v is number | null => v === null || (typeof v === "number" && Number.isFinite(v));

// ---------- rekenen ----------

interface Cell {
  monday: string; label: string;
  cap: number; load: number; pct: number | null;
  /** waaruit de load bestaat: taken met een schatting, verdeelde achterstand en support */
  taskH: number; blH: number; supH: number;
  tasks: { taskId: string; hours: number }[];
  absent: Absence[]; logged: number | null;
  /** inzetbaar voor de planning (Teams); null als dit scherm het niet weet (de medewerker leest geen perioden) */
  inzet: InzetPeriod | null; inzetPct: number | null;
}

interface PRow {
  id: string; name: string; first: string; init: string; person: Person | null; cp: CapPerson;
  backlog: number; backlogTasks: { taskId: string; hours: number }[]; noEst: string[];
  cells: Cell[];
}

const sumH = (xs: { hours: number }[]) => xs.reduce((a, x) => a + x.hours, 0);
/** 7,5 · 12 · 0,3 */
const num1 = (h: number) => (Math.round(h * 10) / 10).toLocaleString("nl-BE", { maximumFractionDigits: 1 });

/**
 * Rijen per persoon. Zonder klant rekent het gedeelde weekLoad; met een klant dezelfde som, maar alleen op de taken
 * van die klant (week, achterstand en zonder schatting) en zonder support.
 */
function buildRows(c: Capacity, idx: Index, o: CapOptions, inK: ((taskId: string) => boolean) | null, inzet: InzetPeriod[] | null): PRow[] {
  return c.people.map((cp) => {
    const person = idx.people.get(cp.personId) || null;
    const name = person?.name || "Onbekend";
    const backlogTasks = inK ? cp.backlog.tasks.filter((x) => inK(x.taskId)) : cp.backlog.tasks;
    const backlog = inK ? sumH(backlogTasks) : cp.backlog.hours;
    const cells = cp.weeks.map((w, i): Cell => {
      const tasks = inK ? w.tasks.filter((x) => inK(x.taskId)) : w.tasks;
      const taskH = inK ? sumH(tasks) : w.load;
      const blH = o.backlogWeeks > 0 && i < o.backlogWeeks ? backlog / o.backlogWeeks : 0;
      const supH = !inK && o.withSupport && w.cap > 0 ? Math.min(cp.supportPerWeek, w.cap) : 0;
      const { load, pct } = inK ? { load: taskH + blH, pct: w.cap > 0 ? Math.round(((taskH + blH) / w.cap) * 100) : null } : weekLoad(cp, i, o);
      const monday = c.weeks[i]?.monday || "";
      const e = inzet ? periodAt(inzet, cp.personId, monday) : null;
      return {
        monday, label: c.weeks[i]?.label || "", cap: w.cap, load, pct, taskH, blH, supH, tasks: [...tasks].sort((a, b) => b.hours - a.hours),
        absent: w.absent, logged: w.logged, inzet: e, inzetPct: inzet ? (e ? e.pct : 100) : null,
      };
    });
    return {
      id: cp.personId, name, first: person?.first || firstName(name), init: person?.initials || initials(name), person, cp,
      backlog, backlogTasks: [...backlogTasks].sort((a, b) => b.hours - a.hours), noEst: inK ? cp.noEstimate.filter(inK) : cp.noEstimate, cells,
    };
  });
}

/** Per week de bezetting van een groep: alle load tegenover alle capaciteit. */
function sumWeeks(rows: PRow[], n: number, th: Thresholds) {
  return Array.from({ length: n }, (_, i) => {
    let l = 0, c = 0;
    for (const r of rows) { l += r.cells[i]?.load || 0; c += r.cells[i]?.cap || 0; }
    const pct = c > 0 ? Math.round((l / c) * 100) : null;
    return { load: l, cap: c, pct, text: pct == null ? "–" : pct + " %", cls: pct == null ? "" : pct > th.over ? "t-err" : pct >= th.full ? "t-warn" : "", tip: num1(l) + " van " + fmtHours(c) + " gepland" };
  });
}

const uniq = (rows: PRow[]) => new Set(rows.flatMap((r) => r.noEst)).size;

// ---------- tonen ----------

type Tone = ReturnType<typeof loadTone>;
const HEAT: Record<Tone, string> = { afwezig: "h-off", ruimte: "h-low", normaal: "h-norm", vol: "h-warn", overvol: "h-err" };
const PILL: Record<Tone, string> = { afwezig: "off", ruimte: "off", normaal: "ok", vol: "warn", overvol: "err" };
const KIND: Record<Absence["kind"], string> = { verlof: "Verlof", feestdag: "Feestdag", sluiting: "Sluiting" };
const span = (a: Absence) => (a.from === a.to ? ddmm(a.from) : ddmm(a.from) + "–" + ddmm(a.to));
/** De afwezigheid die het scherm noemt: verlof gaat voor op een feestdag of sluitingsdag. */
const mainAbsence = (abs: Absence[]) => abs.find((a) => a.kind === "verlof") || abs[0] || null;
const weekdays = (a: Absence) => { let n = 0; for (let d = a.from; d <= a.to; d = addDays(d, 1)) if (dow(d) < 5) n++; return n; };
/** Kort genoeg voor een cel naast de uren: "11/11 vrij", "verlof 21/10" of "3 d verlof"; de datums staan in de tip en de lade. */
const absShort = (a: Absence) => (a.from === a.to ? (a.kind === "verlof" ? "verlof " + ddmm(a.from) : ddmm(a.from) + " vrij") : weekdays(a) + " d " + (a.kind === "verlof" ? "verlof" : "vrij"));
const absLong = (abs: Absence[]) => abs.map((a) => (a.kind === "verlof" ? "verlof" : a.kind) + " " + span(a) + (a.label ? " (" + a.label + ")" : "")).join(", ");
const inzetWhy = (e: InzetPeriod | null) => (e && e.pct < 100 && e.kind ? e.kind.toLowerCase() + (e.note ? ", " + e.note : "") : "");

/** Een cel: Verlof (gearceerd) als de hele week afwezig is, anders gepland tegenover capaciteit, met kleur enkel bij afwijkingen. */
function cellView(r: PRow, c: Cell, unit: Unit, th: Thresholds, klantMode: boolean) {
  const who = r.name + " · " + c.label + ": ";
  if (c.cap <= 0) {
    const a = mainAbsence(c.absent);
    if (a) return { tone: "afwezig" as Tone, cls: "p-heat h-off", main: KIND[a.kind], small: span(a), tip: who + absLong(c.absent) + (c.load >= 0.5 ? "; toch " + num1(c.load) + " u gepland" : "") };
    const why = inzetWhy(c.inzet);
    return { tone: "afwezig" as Tone, cls: "p-heat " + (c.load >= 0.5 ? "h-err" : "h-low"), main: Math.round(c.load) + " / 0", small: c.inzetPct === 0 ? "0 % inzetbaar" : "niet inzetbaar", tip: who + "niet inzetbaar voor de planning" + (why ? ", " + why : "") };
  }
  let tone = loadTone(c.pct, th);
  // met een klant gekozen is weinig uren geen vrije ruimte: dan kleurt alleen vol en overvol
  if (klantMode && tone === "ruimte") tone = "normaal";
  const a = mainAbsence(c.absent), less = c.inzetPct != null && c.inzetPct < 100, why = inzetWhy(c.inzet);
  return {
    tone, cls: "p-heat " + HEAT[tone],
    main: unit === "uren" ? Math.round(c.load) + " / " + Math.round(c.cap) : c.pct + " %",
    small: less ? c.inzetPct + " % inzetbaar" : a ? absShort(a) : "",
    tip: who + num1(c.load) + " van " + fmtHours(c.cap) + " gepland, " + c.pct + " %" + (less ? "; " + c.inzetPct + " % inzetbaar" + (why ? ", " + why : "") : "") + (c.absent.length ? "; " + absLong(c.absent) : ""),
  };
}

function ClockifyNotice({ s, planner, today }: { s: ClockifyStatus; planner: boolean; today: string }) {
  if (s.state === "uit") {
    return (
      <div className="ex-notice ex-notice--mist p-notice"><Icon name="info" size={16} />
        <p><b>Clockify is nog niet gekoppeld.</b> De capaciteit komt uit Instellingen (werkweek en sluitingsdagen). Schattingen, verlof en support volgen met de koppeling; tot dan telt geen taak mee in de uren.</p>
      </div>
    );
  }
  if (s.state === "wacht") {
    return <div className="ex-notice ex-notice--mist p-notice"><Icon name="info" size={16} /><p>De eerste ronde met Clockify loopt nog: schattingen, verlof en gelogde uren verschijnen hier binnen enkele minuten.</p></div>;
  }
  if (s.state === "fout") {
    return (
      <div className="ex-notice ex-notice--warning p-notice"><Icon name="alert" size={16} />
        <p>{s.errorKind === "toegang" ? "Clockify weigert de toegang" : "De laatste ronde met Clockify mislukte"}{planner && s.error ? ": " + s.error : ""}.{s.lastOk ? " U ziet de uren van " + when(s.lastOk, today) + "." : ""}</p>
      </div>
    );
  }
  return null;
}

/** Zonder schatting: waarom, in woorden. */
function hoursState(h: TaskHours | undefined) {
  if (!h) return "geen Clockify-taak";
  if (h.none) return "bewust geen Clockify-taak";
  if (h.missing) return "Clockify-taak bestaat niet meer";
  return "Clockify-taak zonder schatting";
}

// ---------- scherm ----------

export function Capaciteit() {
  const { data: me } = useMe();
  const sq = useSettings();
  const med = me?.role === "medewerker";
  // de medewerker ziet de capaciteit alleen als Instellingen dat toelaat; de server weigert anders met 403
  const blocked = med && !!sq.data && !rightsOf(sq.data.settings).seeCapacity;
  const allowed = !!me && (!med || (!!sq.data && !blocked));
  const cq = useCapacity(allowed);
  const { data, error, refetch } = usePlanning();
  if (blocked || (cq.error instanceof ApiError && cq.error.status === 403)) return <NotAllowed />;
  if ((!data && error) || (!cq.data && cq.error)) return <LoadError what="De capaciteit" onRetry={() => { refetch(); cq.refetch(); }} />;
  if (!data || !me || !cq.data) return <div className="p-loading">Capaciteit laden…</div>;
  return <CapView cap={cq.data} data={data} me={me} />;
}

function NotAllowed() {
  return (
    <>
      <div className="p-head"><div><h1>Planning</h1><p>Bezetting per week</p></div></div>
      <div className="p-tabrow"><PlanningTabs current="capaciteit" /></div>
      <div className="ex-notice ex-notice--mist p-notice"><Icon name="info" size={16} />
        <p>De bezetting van het team bekijkt de planner. Uw eigen taken en week vindt u in <Link className="ex-link" to="/mijn-werk">Mijn werk</Link>.</p>
      </div>
    </>
  );
}

function CapView({ cap, data, me }: { cap: Capacity; data: PlanningData; me: Me }) {
  const idx = useIndex(data);
  const { klant, setKlant } = useAppState();
  const s = useSettings().data?.settings;
  const { data: teamsData } = useTeams();
  const { data: live } = useClockifyLive();
  const ops = useTaskOps();
  const est = useClockifyWrite();
  const teamWrite = useTeamWrite();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [unit, setUnit] = usePref<Unit>("tp.cap.unit", "uren", isUnit);
  const [spread, setSpread] = usePref<number>("tp.cap.spread", 4, isNum);
  const [supOn, setSupOn] = usePref<boolean>("tp.cap.sup", true, isBool);
  const [teamF, setTeamF] = usePref<number | null>("tp.cap.team", null, isTeam);
  const [dd, setDd] = useState<"spr" | "team" | null>(null);
  const [open, setOpen] = useState<Open | null>(null);
  const [expOpen, setExpOpen] = useState(false);

  const today = data.today;
  const planner = me.role === "planner", med = !planner;
  const th: Thresholds = { room: s?.room ?? 50, full: s?.full ?? 90, over: s?.over ?? 100 };
  const weeks = cap.weeks, n = weeks.length;
  const N = Math.min(spread, n); // verdelen over meer weken dan in beeld kan niet
  const inzet = planner ? teamsData?.inzet ?? null : null;
  const kName = (id: string) => idx.klanten.get(id)?.name || "";
  const klantName = klant ? kName(klant) || "deze klant" : null;

  // met een klant gekozen: alleen de uren van de taken van die klant; voor wie ruimte heeft geldt altijd alles
  const full = useMemo(() => buildRows(cap, idx, { backlogWeeks: N, withSupport: supOn }, null, inzet), [cap, idx, N, supOn, inzet]);
  const rows = useMemo(
    () => (klant ? buildRows(cap, idx, { backlogWeeks: N, withSupport: false }, (id) => idx.tasks.get(id)?.projectId === klant, inzet) : full),
    [cap, idx, N, klant, inzet, full],
  );

  // per team een kopregel met het teamtotaal, daarna de mensen op naam; Zonder team achteraan
  const teams = teamsData?.teams || [];
  const teamOf = teamOfPerson(teams);
  const sorted = [...rows].sort((a, b) => a.name.localeCompare(b.name));
  const allGroups = teams.length
    ? [...teams.map((t) => ({ id: t.id as number | null, name: t.name })), { id: null as number | null, name: NO_TEAM }]
      .map((g) => ({ ...g, rows: sorted.filter((r) => (teamOf.get(r.id)?.id ?? null) === g.id) })).filter((g) => g.rows.length)
    : [{ id: null as number | null, name: "", rows: sorted }];
  const team: Team | null = teamF !== null ? teams.find((t) => t.id === teamF) || null : null;
  const groups = team ? allGroups.filter((g) => g.id === team.id) : allGroups;
  const inView = groups.flatMap((g) => g.rows);
  const totB = inView.reduce((a, r) => a + r.backlog, 0), totNe = uniq(inView);
  const tot = sumWeeks(inView, n, th);
  const top = [...inView].sort((a, b) => b.backlog - a.backlog)[0];

  // werkweek in de kop: de meest voorkomende, "40 u per week"
  const hoursText = (() => {
    const c = new Map<string, number>();
    for (const p of cap.people) { const k = num1(p.weekHours); c.set(k, (c.get(k) || 0) + 1); }
    const best = [...c.entries()].sort((a, b) => b[1] - a[1])[0];
    return best ? (c.size > 1 ? "meestal " : "") + best[0] + " u per week" : "";
  })();

  const busy = new Set((live?.timers || []).map((t) => t.personId));
  const dot = (r: PRow) => {
    if (!live || !r.cp.clockifyUserId) return null;
    if (busy.has(r.id)) return { cls: "bezig", title: "Nu bezig volgens de timer in Clockify" };
    if (r.cells[0]?.absent.some((a) => a.from <= today && a.to >= today)) return { cls: "afwezig", title: "Vandaag afwezig" };
    return { cls: "laatst", title: "Geen lopende timer" };
  };
  const linked = cap.clockify.state !== "uit";
  const subOf = (r: PRow) => {
    const sup = supOn && !klant && r.cp.supportPerWeek > 0 ? " · support ≈ " + num1(r.cp.supportPerWeek) + " u" : "";
    return (
      <>{med && r.id === me.id ? "Uw rij · " : ""}{num1(r.cp.weekHours)} u/w{sup}
        {linked && !r.cp.clockifyUserId && <span className="p-cap-nc" title="Niet gevonden in Clockify op e-mailadres: de capaciteit komt uit Instellingen, zonder verlof en gelogde uren"> · niet in Clockify</span>}
      </>
    );
  };

  const sel = params.get("taak");
  const selTask = sel ? data.tasks.find((t) => t.id === sel) : null;
  const openTask = (id: string) => setParams((p) => { p.set("taak", id); return p; });
  const closeTask = () => setParams((p) => { p.delete("taak"); return p; });
  const show = (o: Open) => { setOpen(o); setDd(null); setExpOpen(false); };
  const isSel = (kind: Open["kind"], pid: string, week = -1) => !!open && open.kind === kind && open.personId === pid && (open.kind !== "cell" || open.week === week);
  const openRow = open ? rows.find((r) => r.id === open.personId) || null : null;

  // wijzigingen: naar Planner (eigenaar, datums), Clockify (schatting) of Teams (inzetbaar); daarna de capaciteit opnieuw
  const refresh = () => qc.invalidateQueries({ queryKey: ["capacity"] });
  const runOps = (t: Task, o: TaskOp[], msg: string) => ops.run(t, o, msg).then(refresh, () => {});
  const act: Actions = {
    reassign: (t, from, to) => {
      const o: TaskOp[] = [{ kind: "owner", personId: from, on: false }];
      if (!t.owners.some((x) => x.personId === to.id)) o.push({ kind: "owner", personId: to.id, on: true });
      runOps(t, o, "Naar " + to.first + ": " + t.title);
    },
    later: (t) => {
      const start = addDays(t.start > today ? t.start : today, 7), end = addDays(t.end, 7);
      runOps(t, [{ kind: "dates", start, end }], "Een week later, deadline " + ddmm(end) + ": " + t.title);
    },
    newEnd: (t, f) => runOps(t, endOps(t, f), "Nieuwe deadline " + dayDate(f) + ": " + t.title),
    estimate: (t, h) => { est.run(() => clockifyApi.estimate(t.id, h)).catch(() => {}); },
    setInzet: (r, c, pct) => {
      teamWrite.run({
        run: () => teamsApi.addInzet({ personId: r.id, from: c.monday, to: c.monday, pct, kind: pct < 100 ? "Anders" : "", note: pct < 100 ? "aangepast in Capaciteit" : "" }),
        undo: (res) => teamsApi.removeInzet(res.id as number),
        keys: [["teams"], ["capacity"]],
      }).catch(() => {});
    },
    busy: est.busy || teamWrite.busy,
  };

  const teamSub = (rs: PRow[]) => {
    const less = inzet ? rs.filter((r) => r.cells.some((c) => c.inzetPct != null && c.inzetPct < 100)).length : 0;
    return nOf(rs.length, "persoon", "personen") + (less ? " · " + less + " minder inzetbaar" : "");
  };
  const exportSub = nOf(inView.length, "persoon", "personen") + " · " + n + " weken · " + (team ? "team " + team.name : teams.length ? "alle teams" : "iedereen")
    + (klantName ? " · klant " + klantName : " · support " + (supOn ? "meegeteld" : "niet meegeteld")) + " · achterstand " + (N ? "over " + N + " weken" : "niet meegeteld");

  return (
    <>
      <div className="p-head">
        <div><h1>Planning</h1><p>Bezetting per week · schatting min gelogd uit Clockify, gespreid tot de Planner-deadline{hoursText ? " · " + hoursText : ""}{med ? " · alleen bekijken" : ""}</p></div>
        {planner && <div className="p-tools"><button type="button" className="ex-btn" onClick={() => { setExpOpen(true); setOpen(null); setDd(null); }}><Icon name="download" size={15} />Exporteren</button></div>}
      </div>
      <div className="p-tabrow">
        <PlanningTabs current="capaciteit" />
        <div className="p-tools p-cap-tools">
          <div className="ex-seg" role="group" aria-label="Eenheid">
            <button type="button" aria-pressed={unit === "uren"} onClick={() => setUnit("uren")}>Uren</button>
            <button type="button" aria-pressed={unit === "pct"} onClick={() => setUnit("pct")}>Procent</button>
          </div>
          <div className="p-ddwrap">
            <button type="button" className="ex-btn p-dd" aria-haspopup="true" aria-expanded={dd === "spr"} onClick={() => setDd(dd === "spr" ? null : "spr")}>Achterstand<span className="p-dd-v">{N ? "over " + N + " weken" : "niet meetellen"}</span><Icon name="down" size={14} /></button>
            {dd === "spr" && <>
              <button type="button" className="p-dd-scrim" aria-label="Sluiten" onClick={() => setDd(null)} />
              <div className="p-pop p-pop-s" role="dialog" aria-label="Achterstand verdelen">
                <div className="p-pop-h"><span>Achterstand verdelen</span></div>
                <div className="p-pop-b">
                  {[...SPREADS.filter((v) => v <= n), 0].map((v) => (
                    <button key={v} type="button" className={"p-opt1" + (N === v ? " on" : "")} onClick={() => { setSpread(v); setDd(null); }}>
                      <span className={"p-radio" + (N === v ? " on" : "")} />{v ? "Verdelen over " + v + " weken" : "Niet meetellen"}
                    </button>
                  ))}
                </div>
                <div className="p-pop-f">Resterende uren van taken met een verlopen deadline</div>
              </div>
            </>}
          </div>
          {teams.length > 0 && (
            <div className="p-ddwrap">
              <button type="button" className="ex-btn p-dd" aria-haspopup="true" aria-expanded={dd === "team"} onClick={() => setDd(dd === "team" ? null : "team")}>Team<span className="p-dd-v">{team ? team.name : "alle"}</span><Icon name="down" size={14} /></button>
              {dd === "team" && <>
                <button type="button" className="p-dd-scrim" aria-label="Sluiten" onClick={() => setDd(null)} />
                <div className="p-pop p-pop-s p-pop-r" role="dialog" aria-label="Team kiezen">
                  <div className="p-pop-b">
                    {[{ label: "Alle teams", v: null as number | null }, ...teams.map((t) => ({ label: t.name + " · " + t.members.length, v: t.id as number | null }))].map((o) => (
                      <button key={o.v ?? "alle"} type="button" className={"p-opt1" + ((team?.id ?? null) === o.v ? " on" : "")} onClick={() => { setTeamF(o.v); setDd(null); }}>
                        <span className={"p-radio" + ((team?.id ?? null) === o.v ? " on" : "")} />{o.label}
                      </button>
                    ))}
                  </div>
                  <div className="p-pop-f">{planner ? <>Teams richt u in onder <Link className="ex-link" to="/teams">Teams</Link></> : "Teams richt de planner in"}</div>
                </div>
              </>}
            </div>
          )}
          <label className="ex-check p-check" title={klant ? "Met een klant gekozen telt support niet mee" : undefined}>
            <input type="checkbox" checked={supOn && !klant} disabled={!!klant} onChange={() => setSupOn(!supOn)} />Support meetellen
          </label>
        </div>
      </div>
      <ClockifyNotice s={cap.clockify} planner={planner} today={today} />
      {klantName && (
        <div className="p-fnote"><Icon name="search" size={14} />
          <span>Alleen de uren van taken van {klantName}; support telt niet mee en kleur toont enkel wie vol of overvol loopt.</span>
          <button type="button" className="ex-link" onClick={() => setKlant(null)}>Alle klanten</button>
        </div>
      )}
      <div className="ex-notice ex-notice--info p-notice p-cap-note"><Icon name="info" size={16} />
        <p>
          {totB >= 0.5
            ? <><b>{Math.round(totB)} u achterstand</b> (deadline voorbij) {N ? "is verdeeld over de volgende " + N + " weken" : "telt nu niet mee in de weken"}; herplan die taken voor een juiste spreiding.</>
            : "Geen achterstand met een schatting."}{" "}
          {totNe ? <><b>{count(totNe)} zonder schatting</b> {totNe === 1 ? "telt" : "tellen"} niet mee.</> : "Elke actieve taak heeft een schatting."}
        </p>
        {top && top.backlog >= 0.5 && <button type="button" className="ex-btn-xs" onClick={() => show({ kind: "backlog", personId: top.id })}>Grootste achterstand: {top.first}</button>}
      </div>
      <section className="p-card p-cap" style={{ "--cap-n": n } as CSSProperties}>
        <div className="p-cap-scroll">
          <div className="p-cap-row p-cap-hd">
            <span className="p">Persoon</span><span>Achterstand</span>
            {weeks.map((w, i) => <span key={w.monday} className={i === 0 ? "now" : ""}>{w.label}<small>{i === 0 ? "deze week" : ddmm(w.monday)}</small></span>)}
            <span>Zonder schatting</span>
          </div>
          {groups.map((g) => (
            <Fragment key={g.id ?? "geen"}>
              {teams.length > 0 && (
                <div className="p-cap-row p-cap-team">
                  <span className="p"><span className="nm"><b>{g.name}</b><small>{teamSub(g.rows)}</small></span></span>
                  <span className="v">{Math.round(g.rows.reduce((a, r) => a + r.backlog, 0))} u</span>
                  {sumWeeks(g.rows, n, th).map((w, i) => <span key={i} className={w.cls} title={w.tip}>{w.text}</span>)}
                  <span className="v">{uniq(g.rows) || "–"}</span>
                </div>
              )}
              {g.rows.map((r) => {
                const st = dot(r), ne = r.noEst.length, nb = r.backlogTasks.length;
                return (
                  <div key={r.id} className={"p-cap-row" + (med && r.id === me.id ? " p-cap-me" : "")}>
                    <span className="p">
                      <span className="p-avwrap"><Av id={r.id} init={r.init} />{st && <span className={"st " + st.cls} title={st.title} />}</span>
                      <span className="nm"><b>{r.name}</b><small>{subOf(r)}</small></span>
                    </span>
                    <button type="button" className={"p-heat h-bl" + (r.backlog > (r.cp.weekHours || 40) ? " hi" : "") + (isSel("backlog", r.id) ? " sel" : "")}
                      title={"Achterstand van " + r.name + ": " + num1(r.backlog) + " u in " + count(nb)} onClick={() => show({ kind: "backlog", personId: r.id })}>
                      {Math.round(r.backlog)} u<small>{count(nb)}</small>
                    </button>
                    {r.cells.map((c, i) => {
                      const v = cellView(r, c, unit, th, !!klant);
                      return (
                        <button key={c.monday || i} type="button" className={v.cls + (isSel("cell", r.id, i) ? " sel" : "")} title={v.tip} onClick={() => show({ kind: "cell", personId: r.id, week: i })}>
                          {v.main}{v.small && <small>{v.small}</small>}
                        </button>
                      );
                    })}
                    <button type="button" className={"p-noest" + (ne ? " has" : "") + (isSel("noest", r.id) ? " sel" : "")} title={ne ? count(ne) + " zonder schatting" : "Alle taken hebben een schatting"}
                      onClick={() => { if (ne) show({ kind: "noest", personId: r.id }); }}>
                      {ne || "–"}
                    </button>
                  </div>
                );
              })}
            </Fragment>
          ))}
          {!inView.length && <p className="p-empty">{team ? "Niemand van team " + team.name + " op de planning." : "Nog niemand op de planning."}</p>}
          <div className="p-cap-row p-cap-tot">
            <span className="p">{team ? "Team " + team.name : "Hele team"}</span>
            <span>{Math.round(totB)} u</span>
            {tot.map((w, i) => <span key={i} className={w.cls} title={w.tip}>{w.text}</span>)}
            <span className={totNe ? "t-warn" : ""}>{totNe || "–"}</span>
          </div>
        </div>
      </section>
      <div className="p-cap-legend">
        {!klant && <span><span className="p-sw" style={{ background: "var(--mist)", borderColor: "var(--mist)" }} /> Ruimte, onder {th.room} %</span>}
        <span><span className="p-sw" /> Normaal</span>
        <span><span className="p-sw" style={{ background: "var(--warn-bg)", borderColor: "var(--warn-bd)" }} /> Vol, {th.full} tot {th.over} %</span>
        <span><span className="p-sw" style={{ background: "var(--err-bg)", borderColor: "var(--err-bd)" }} /> Overvol, boven {th.over} %</span>
        <span><span className="p-sw h-off" /> Verlof</span>
        {inzet && <span><b className="p-cap-inz">50 % inzetbaar</b> deel van de week buiten de planning</span>}
        <span className="p-cap-hint">Klik op een cel voor de taken van die week</span>
      </div>
      {open && openRow && !selTask && (
        <CapDrawer key={JSON.stringify(open)} o={open} row={openRow} cap={cap} data={data} idx={idx} me={me} full={full} N={N} klantName={klantName}
          th={th} act={act} onOpen={show} onTask={openTask} onClose={() => setOpen(null)} />
      )}
      {expOpen && planner && (
        <ExportDrawer rows={inView} weeks={weeks} teamOf={(r) => teamOf.get(r.id)?.name || (teams.length ? NO_TEAM : "")} totLabel={team ? "Team " + team.name : "Hele team"}
          totB={totB} totNe={totNe} tot={tot} sub={exportSub} slug={klantName} onClose={() => setExpOpen(false)} />
      )}
      {selTask && <TaskDrawer key={selTask.id} task={selTask} data={data} onClose={closeTask} />}
    </>
  );
}

// ---------- lade: de taken achter een cel, de achterstand of zonder schatting ----------

interface Actions {
  reassign: (t: Task, from: string, to: PRow) => void;
  later: (t: Task) => void;
  newEnd: (t: Task, friday: string) => void;
  estimate: (t: Task, hours: number) => void;
  setInzet: (r: PRow, c: Cell, pct: number) => void;
  busy: boolean;
}

interface DrawerProps {
  o: Open; row: PRow; cap: Capacity; data: PlanningData; idx: Index; me: Me;
  /** alle uren, ook met een klant gekozen: wie heeft ruimte */
  full: PRow[];
  N: number; klantName: string | null; th: Thresholds; act: Actions;
  onOpen: (o: Open) => void; onTask: (taskId: string) => void; onClose: () => void;
}

function CapDrawer({ o, row, cap, data, idx, me, full, N, klantName, th, act, onOpen, onTask, onClose }: DrawerProps) {
  const [cand, setCand] = useState<string | null>(null);
  const today = data.today, planner = me.role === "planner", write = planner && me.canWrite;
  const canEst = planner && cap.clockify.state !== "uit";
  const mine = row.id === me.id;
  const week = o.kind === "cell" ? o.week : 0;
  const c = row.cells[week];
  if (!c) return null;

  // wie heeft ruimte in die week: de meeste vrije uren eerst, wie al eigenaar is niet
  const cands = (t: Task, i: number) => full
    .filter((r) => r.id !== row.id && !t.owners.some((x) => x.personId === r.id) && (r.cells[i]?.cap || 0) > 0)
    .map((r) => ({ r, free: r.cells[i].cap - r.cells[i].load, pct: r.cells[i].pct ?? 0 }))
    .sort((a, b) => b.free - a.free).slice(0, 4);

  const item = (id: string, hours: number, mode: Open["kind"]) => {
    const t = idx.tasks.get(id) || null, h = cap.tasks[id];
    const title = t?.title || h?.name || "Taak niet meer op het bord";
    const kl = t ? idx.klanten.get(t.projectId)?.name || "" : h?.client || "";
    const d = t ? deadline(t.end, today) : null;
    const sub = [kl,
      t && d ? (mode === "backlog" ? "deadline " + ddmm(t.end) + (d.od > 0 ? ", " + nOf(d.od, "dag", "dagen") + " geleden" : "") : "deadline " + d.text) : "",
      mode === "noest" ? hoursState(h) : ""].filter(Boolean).join(" · ");
    const candOpen = cand === id;
    const i = mode === "cell" ? week : 0;
    const canMove = write && !!t && mode !== "noest";
    const later = write && !!t && mode === "cell" && !!d && d.od <= 0;
    const ends = write && !!t && mode === "backlog";
    const ests = canEst && !!t && mode === "noest";
    const link = planner && !write && !!t && mode !== "noest";
    const ks = candOpen && t ? cands(t, i) : [];
    return (
      <div key={id} className="p-capi">
        <div className="p-capi-h">
          {t ? <button type="button" className="main p-capi-t" onClick={() => onTask(t.id)} title="Taak openen"><b>{title}</b><span>{sub}</span></button>
            : <span className="main"><b>{title}</b><span>{sub}</span></span>}
          {mode !== "noest" && <span className="p-capi-hh"><b>{num1(hours)} u</b><small>{mode === "cell" ? "in deze week" : "resterend"}</small></span>}
        </div>
        {(canMove || later || ends || ests || link) && t && (
          <div className="p-quick">
            {ends && fridays(today).map((f) => <button key={f} type="button" className="p-tog" disabled={act.busy} onClick={() => act.newEnd(t, f)}><Icon name="cal" size={13} />vr {ddmm(f)}</button>)}
            {ests && EST_PICKS.map((e) => <button key={e} type="button" className="p-tog" disabled={act.busy} onClick={() => act.estimate(t, e)}>{e} u</button>)}
            {canMove && <button type="button" className={"p-tog" + (candOpen ? " on" : "")} aria-expanded={candOpen} onClick={() => setCand(candOpen ? null : id)}><Icon name="users" size={13} />Naar collega</button>}
            {later && <button type="button" className="p-tog" disabled={act.busy} onClick={() => act.later(t)}><Icon name="right" size={13} />Week later</button>}
            {link && <a className="ex-btn-xs" href={plannerTaskUrl(me, t)} target="_blank" rel="noreferrer" title="Deze taak openen in Planner"><Icon name="ext" size={13} />Openen in Planner</a>}
          </div>
        )}
        {candOpen && t && (
          <div className="p-capc">
            {ks.map((k) => (
              <button key={k.r.id} type="button" className="p-cand" disabled={act.busy} onClick={() => { setCand(null); act.reassign(t, row.id, k.r); }}>
                <Av id={k.r.id} init={k.r.init} />
                <span><b>{k.r.name}</b><span className={"p-capc-n " + (k.free >= 8 ? "t-ok" : k.free >= 0 ? "t-warn" : "t-err")}>{(k.free >= 0 ? num1(k.free) + " u vrij" : num1(-k.free) + " u te veel") + " in " + (row.cells[i]?.label || "") + " · nu " + k.pct + " %"}</span></span>
                <span className="p-capc-go">Kiezen</span>
              </button>
            ))}
            {!ks.length && <p className="p-empty">Niemand anders heeft capaciteit in {row.cells[i]?.label || "die week"}.</p>}
          </div>
        )}
      </div>
    );
  };

  const hint = planner
    ? (o.kind === "noest" ? (canEst ? "Schattingen gaan naar de gekoppelde taak in Clockify" : "Schatten kan zodra Clockify gekoppeld is")
      : write ? "Eigenaar en datums gaan naar Planner" : "Wijzigen doet u in Planner; vanuit de app kan dat met een Project-licentie")
    : o.kind === "noest" ? (mine ? "Alleen bekijken · een schatting geeft u in de taak, in Mijn werk" : "Alleen bekijken · schatten doet de eigenaar of de planner")
    : "Alleen bekijken · herverdelen doet de planner";

  let eyebrow = "", pill: [string, string] = ["off", ""], sub = "", listTitle = "", empty = "", body: ReactNode = null, list: ReactNode[] = [];
  if (o.kind === "cell") {
    const v = cellView(row, c, "uren", th, !!klantName);
    const a = mainAbsence(c.absent);
    eyebrow = "Capaciteit · " + c.label + " · " + ddmm(c.monday) + " tot " + ddmm(addDays(c.monday, 4)) + (klantName ? " · " + klantName : "");
    pill = c.cap <= 0 ? ["off", a ? KIND[a.kind] + " " + span(a) : "Niet inzetbaar"] : [PILL[v.tone], c.pct + " % · " + num1(c.load) + " van " + fmtHours(c.cap)];
    const less = c.inzetPct != null && c.inzetPct < 100;
    const kv: [string, string][] = [
      ["Capaciteit", fmtHours(c.cap) + (less ? ", " + c.inzetPct + " % inzetbaar" : "") + (c.absent.length ? " · " + absLong(c.absent) : "")],
      [klantName ? "Taken van " + klantName : "Taken in deze week", fmtHours(c.taskH)],
    ];
    if (c.blH) kv.push(["Achterstand, verdeeld over " + N + " weken", fmtHours(c.blH)]);
    if (c.supH) kv.push(["Supportreservering", fmtHours(c.supH)]);
    kv.push(["Totaal gepland", fmtHours(c.load)]);
    if (week === 0 && c.logged != null && !klantName) kv.push(["Al gelogd in Clockify deze week", fmtHours(c.logged)]);
    const blPart = c.load > 0 ? c.blH / c.load : 0;
    const why = inzetWhy(c.inzet);
    body = (
      <>
        <div className="p-fs"><h3>Opbouw van deze week</h3>{kv.map(([k, val]) => <div key={k} className="p-kv"><span>{k}</span><b>{val}</b></div>)}</div>
        {c.inzetPct != null && !(c.cap <= 0 && a) && (
          <div className="p-fs"><h3>Inzetbaar voor de planning</h3>
            <p className="p-kd-note" style={{ margin: "0 0 10px" }}>{less ? c.inzetPct + " % van de week is inzetbaar voor de planning" + (why ? "; de rest gaat naar " + why : "") + "." : "De hele week is inzetbaar voor de planning."}</p>
            <div className="p-quick">
              {INZET_PCTS.map((p) => <button key={p} type="button" className={"p-tog p-tog-s" + (c.inzetPct === p ? " on" : "")} disabled={act.busy} onClick={() => { if (c.inzetPct !== p) act.setInzet(row, c, p); }}>{p} %</button>)}
            </div>
            <p className="p-kd-note">Geldt voor deze week. Voor een langere periode, met een reden: <Link className="ex-link" to="/teams">Teams</Link>.</p>
          </div>
        )}
        {blPart > 0.4 && (
          <div className="ex-notice ex-notice--warning p-notice p-cap-note"><Icon name="alert" size={16} />
            <p>Deze week bestaat voor {Math.round(blPart * 100)} % uit achterstand: taken waarvan de deadline al voorbij is. {planner ? "Herplan die taken, dan klopt de spreiding weer." : mine ? "Stel nieuwe datums voor in Mijn werk, dan klopt de spreiding weer." : "Als die taken een nieuwe datum krijgen, klopt de spreiding weer."}</p>
            <button type="button" className="ex-btn-xs" onClick={() => onOpen({ kind: "backlog", personId: row.id })}>Achterstand bekijken</button>
          </div>
        )}
      </>
    );
    listTitle = "Taken in deze week · " + c.tasks.length;
    list = c.tasks.map((x) => item(x.taskId, x.hours, "cell"));
    empty = "Geen taken met uren in deze week.";
  } else if (o.kind === "backlog") {
    const lateNoEst = row.noEst.filter((id) => { const t = idx.tasks.get(id); return !!t && t.end < today; }).length;
    eyebrow = "Capaciteit · achterstand" + (klantName ? " · " + klantName : "");
    pill = ["warn", Math.round(row.backlog) + " u in " + count(row.backlogTasks.length)];
    sub = "Resterende uren van taken met een verlopen deadline. " + (N ? "Ze worden nu verdeeld over de komende " + N + " weken. " : "Ze tellen nu niet mee in de weken. ")
      + (planner ? "Een nieuwe deadline plant ze in de juiste weken." : mine ? "Nieuwe datums stelt u voor in Mijn werk, onder Achterstand opruimen." : "Een nieuwe deadline, gezet door de planner, plant ze in de juiste weken.")
      + (lateNoEst ? " Nog " + count(lateNoEst) + " met een verlopen deadline " + (lateNoEst === 1 ? "heeft" : "hebben") + " geen schatting en " + (lateNoEst === 1 ? "telt" : "tellen") + " niet mee." : "");
    listTitle = "Verlopen taken · " + row.backlogTasks.length;
    list = row.backlogTasks.map((x) => item(x.taskId, x.hours, "backlog"));
    empty = "Geen achterstand.";
  } else {
    const ids = [...row.noEst].sort((a, b) => (idx.tasks.get(a)?.end || "").localeCompare(idx.tasks.get(b)?.end || ""));
    eyebrow = "Capaciteit · zonder schatting" + (klantName ? " · " + klantName : "");
    pill = ["warn", count(ids.length) + " zonder schatting"];
    sub = "Deze actieve taken hebben geen schatting in Clockify en tellen daarom niet mee in de bezetting." + (canEst ? " Een schatting telt meteen mee." : "");
    listTitle = "Taken zonder schatting · " + ids.length;
    list = ids.map((id) => item(id, 0, "noest"));
    empty = "Alle taken hebben een schatting.";
  }

  return (
    <>
      <button type="button" className="p-scrim" aria-label="Sluiten" onClick={onClose} />
      <aside className="p-drawer" role="dialog" aria-modal="true" aria-labelledby="ct">
        <div className="p-drawer-h">
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
            <div style={{ minWidth: 0 }}><p className="ex-eyebrow" style={{ margin: 0 }}>{eyebrow}</p><h2 id="ct">{row.name}</h2></div>
            <button type="button" className="ex-icon-btn ex-icon-btn--square" aria-label="Sluiten" title="Sluiten" onClick={onClose}><Icon name="x" size={18} /></button>
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginTop: 10 }}>
            <span className={"ex-pill ex-pill--" + pill[0]}><span className="ex-dot" />{pill[1]}</span>
          </div>
          {sub && <p className="p-drawer-sub">{sub}</p>}
        </div>
        <div className="p-drawer-b p-capd">
          {body}
          <div className="p-fs"><h3>{listTitle}</h3>
            <div className="p-capl">
              {list}
              {!list.length && <p className="p-empty">{empty}</p>}
            </div>
          </div>
        </div>
        <div className="p-drawer-f"><span className="t-mute" style={{ fontSize: 13 }}>{hint}</span><button type="button" className="ex-btn" onClick={onClose}>Sluiten</button></div>
      </aside>
    </>
  );
}

// ---------- exporteren: wat nu in beeld staat, als CSV voor Excel ----------

interface ExportProps {
  rows: PRow[]; weeks: Capacity["weeks"]; teamOf: (r: PRow) => string; totLabel: string;
  totB: number; totNe: number; tot: { load: number; cap: number; pct: number | null }[];
  sub: string; slug: string | null; onClose: () => void;
}

const csvNum = (h: number) => String(Math.round(h * 10) / 10).replace(".", ",");
const csvCell = (s: string) => (/[;"\r\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s);
const slugOf = (s: string) => s.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

/** Eén regel per persoon, per week gepland, capaciteit en bezetting, en onderaan het totaal; puntkomma's en decimale komma. */
function toCsv(p: ExportProps): string {
  const head = ["Persoon", "Team", "Achterstand (u)", ...p.weeks.flatMap((w) => [w.label + " gepland (u)", w.label + " capaciteit (u)", w.label + " bezetting (%)"]), "Zonder schatting"];
  const lines = p.rows.map((r) => [r.name, p.teamOf(r), csvNum(r.backlog), ...r.cells.flatMap((c) => [csvNum(c.load), csvNum(c.cap), c.pct == null ? "" : String(c.pct)]), String(r.noEst.length)]);
  const total = [p.totLabel, "", csvNum(p.totB), ...p.tot.flatMap((w) => [csvNum(w.load), csvNum(w.cap), w.pct == null ? "" : String(w.pct)]), String(p.totNe)];
  return [head, ...lines, total].map((x) => x.map(csvCell).join(";")).join("\r\n");
}

function ExportDrawer(p: ExportProps) {
  const toast = useToast();
  const pre = useRef<HTMLPreElement>(null);
  const csv = toCsv(p), W = p.weeks;
  const file = "bezetting-" + (W[0]?.label || "").toLowerCase() + "-" + (W[W.length - 1]?.label || "").toLowerCase() + (p.slug ? "-" + slugOf(p.slug) : "") + ".csv";
  const grid: CSSProperties | undefined = W.length !== 8 ? { gridTemplateColumns: "minmax(0,1.5fr) repeat(" + W.length + ",minmax(0,1fr))" } : undefined;
  const download = () => {
    try {
      // met BOM, zodat Excel de tekst als UTF-8 leest
      const url = URL.createObjectURL(new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" }));
      const a = document.createElement("a");
      a.href = url; a.download = file;
      document.body.appendChild(a); a.click(); a.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.flash("Download gestart: " + file);
    } catch {
      toast.flash("Downloaden lukt niet in deze browser; gebruik Kopiëren", { error: true });
    }
  };
  // lukt het klembord niet, dan de tekst selecteren zodat Ctrl+C werkt
  const select = () => {
    try {
      const r = document.createRange(), s = window.getSelection();
      if (!pre.current || !s) throw new Error();
      r.selectNodeContents(pre.current); s.removeAllRanges(); s.addRange(r);
      toast.flash("De tekst is geselecteerd: druk Ctrl+C om te kopiëren");
    } catch { toast.flash("Kopiëren lukt niet in deze browser", { error: true }); }
  };
  const copy = () => {
    if (!navigator.clipboard) { select(); return; }
    navigator.clipboard.writeText(csv).then(() => toast.flash("Gekopieerd: plak het in Excel"), select);
  };
  return (
    <>
      <button type="button" className="p-scrim" aria-label="Sluiten" onClick={p.onClose} />
      <aside className="p-drawer" role="dialog" aria-modal="true" aria-labelledby="ex">
        <div className="p-drawer-h">
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
            <div style={{ minWidth: 0 }}><p className="ex-eyebrow" style={{ margin: 0 }}>Capaciteit · exporteren</p><h2 id="ex">Bezetting als CSV</h2></div>
            <button type="button" className="ex-icon-btn ex-icon-btn--square" aria-label="Sluiten" title="Sluiten" onClick={p.onClose}><Icon name="x" size={18} /></button>
          </div>
          <p className="p-drawer-sub">{p.sub}</p>
        </div>
        <div className="p-drawer-b" style={{ overflowY: "auto" }}>
          <div className="p-fs"><h3>Voorbeeld · gepland en capaciteit in uren</h3>
            <div className="p-exp-t">
              <div className="p-exp-r p-exp-h" style={grid}><span>Persoon</span>{W.map((w) => <span key={w.monday}>{w.label}</span>)}</div>
              {p.rows.slice(0, 6).map((r) => (
                <div key={r.id} className="p-exp-r" style={grid}><span>{r.name}</span>{r.cells.map((c, i) => <span key={i}>{c.cap > 0 ? Math.round(c.load) + "/" + Math.round(c.cap) : "–"}</span>)}</div>
              ))}
            </div>
            {p.rows.length > 6 && <p className="p-kd-note">en nog {nOf(p.rows.length - 6, "persoon", "personen")}</p>}
          </div>
          <div className="p-fs"><h3>Bestand · {file}</h3>
            <pre className="p-exp-csv" ref={pre}>{csv}</pre>
            <p className="p-kd-note">Per week de geplande uren, de capaciteit en de bezetting, met puntkomma's zodat Excel het meteen in kolommen zet. Gebeurt er bij Downloaden niets, kopieer dan de tekst.</p>
          </div>
        </div>
        <div className="p-drawer-f">
          <span className="t-mute" style={{ fontSize: 13 }}>{nOf(p.rows.length, "persoon", "personen")} · wat nu in beeld staat</span>
          <span style={{ display: "flex", gap: 8 }}>
            <button type="button" className="ex-btn" onClick={copy}>Kopiëren</button>
            <button type="button" className="ex-btn ex-btn--primary" onClick={download}><Icon name="download" size={15} />Downloaden</button>
          </span>
        </div>
      </aside>
    </>
  );
}
